import { Card, Form, Spin } from "antd";
import cloneDeep from "lodash/cloneDeep";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import SeverityGridEditor from "@/components/AssetType/SeverityGridEditor";
import FormViewer from "@/components/FormEngine/Viewer";
import { TAB_KEYS } from "@/constants/TabKeys";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { JsonEditor } from "@/ui/json-editor";
import { TabView } from "@/ui/tab-view";

import DragAndDropTool from "./DragAndDropTool";

/**
 * cloneDeep(schema) usage:
 *
 * To prevent schema struct modification between tabs selection:
 * Loading Preview Tab, schema.fields[N].visible (or.values)
 * are anonymous function: required to apply condition rules.
 * Otherwise struct is an array.
 */
const FormPreview = ({ schema, loading, emptyDescription }) => {
  const [form] = Form.useForm();

  return (
    <Card loading={loading}>
      <FormViewer
        schema={cloneDeep(schema)}
        emptyDescription={emptyDescription}
        formParams={{ form }}
      />
    </Card>
  );
};

const FormEditor = ({
  schema,
  context,
  defectTree,
  translation,
  onUpdateSchema,
  onUpdateDefectTree,
  onUpdateTranslation,
  loading,
  emptyDescription,
  isEditable = true,
  parentId,
  syncTabNav,
  tabBarExtraContent,
}) => {
  const { t } = useTranslation();
  const isFeatureSeverityGridEditorOn = useFeatureFlag("dev2579");

  const tabs = [
    {
      key: TAB_KEYS.TEMPLATE_PREVIEW,
      label: t("form:preview"),
      children: (
        <FormPreview
          schema={schema}
          loading={loading}
          emptyDescription={emptyDescription}
        />
      ),
      fullHeight: true,
    },
    {
      key: TAB_KEYS.TEMPLATE_FORM_EDITOR,
      label: t("form:formEditor"),
      children: (
        <Spin spinning={loading}>
          <DragAndDropTool
            schema={schema}
            context={context}
            onSchemaChange={onUpdateSchema}
          />
        </Spin>
      ),
      fullHeight: true,
      hidden: !isEditable,
    },
    {
      key: TAB_KEYS.TEMPLATE_SCHEMA,
      label: t("form:jsonEditor"),
      children: (
        <JsonEditor
          schema={schema}
          onSchemaChange={onUpdateSchema}
          isEditable={isEditable}
          fullHeight
        />
      ),
      fullHeight: true,
    },
    {
      key: TAB_KEYS.TEMPLATE_SEVERITY_GRID,
      label: t("form:severityGridEditor.title"),
      children: (
        <SeverityGridEditor
          assetTypeId={parentId}
          defectTree={defectTree}
          translation={translation}
          onUpdateDefectTree={onUpdateDefectTree}
          onUpdateTranslation={onUpdateTranslation}
          loading={loading}
        />
      ),
      fullHeight: true,
      hidden: !isFeatureSeverityGridEditorOn || !defectTree,
    },
  ];

  return (
    <TabView
      isNav={syncTabNav}
      tabs={tabs}
      isFullHeight
      defaultActiveKey="preview"
      tabBarExtraContent={tabBarExtraContent}
      classNames={{
        root: "form-editor-tabs--root",
        body: "form-editor-tabs--body",
        content: "form-editor-tabs--content",
      }}
    />
  );
};

FormEditor.propTypes = {
  schema: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  context: PropTypes.string.isRequired,
  defectTree: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  translation: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onUpdateSchema: PropTypes.func.isRequired,
  onUpdateDefectTree: PropTypes.func,
  onUpdateTranslation: PropTypes.func,
  loading: PropTypes.bool,
  emptyDescription: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isEditable: PropTypes.bool,
  parentId: PropTypes.number,
  syncTabNav: PropTypes.bool,
  tabBarExtraContent: PropTypes.object,
};

export default FormEditor;
